import { ActionCenterTask, ActionCenterTaskType } from '@domain'
import { alpha } from '@mui/material'
import { ActionCenterKeys } from '@services/actionCenterTasks.api'
import { DirectDealKeys } from '@services/directDeals.api'
import {
	DirectDeal,
	DirectDealCategory,
	DirectDealsOverview,
	DirectDealStatus,
	DirectDealSummary,
} from '@services/directDeals.types'
import { QueryClient } from '@tanstack/react-query'

export const CategoryProps = {
	[DirectDealCategory.RECOMMEND]: {
		label: 'Recommend',
		color: 'success.dark',
		bgcolor: (theme) => alpha(theme.palette.success.dark, 0.08),
	},
	[DirectDealCategory.DECLINE]: {
		label: 'Decline',
		color: 'error.dark',
		bgcolor: (theme) => alpha(theme.palette.error.dark, 0.08),
	},
}

const CATEGORY_PRIORITY: Record<DirectDealCategory, number> = {
	[DirectDealCategory.RECOMMEND]: 0,
	[DirectDealCategory.DECLINE]: 1,
}

export type CacheSnapshot = {
	previousSummaryData: DirectDealsOverview | undefined
	previousPendingListData: DirectDealSummary[] | undefined
	previousTargetListData: DirectDealSummary[] | undefined
	updatedDirectDealData: DirectDeal | undefined
	status: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED
}

/**
 * Captures the current state of all relevant direct deal caches before mutation
 */
export const snapshotDirectDealCaches = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	directDealId: string | number,
	targetStatus: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED,
	activeTab: 'ALL' | 'STOCKED' | 'NOT_STOCKED'
): Omit<CacheSnapshot, 'status'> => {
	const previousSummaryData = queryClient.getQueryData<DirectDealsOverview | undefined>(
		DirectDealKeys.summary(marketBasketId)
	)

	const pendingListKey = DirectDealKeys.list(marketBasketId, {
		status: DirectDealStatus.PENDING,
	})
	const previousPendingListData = queryClient.getQueryData<DirectDealSummary[] | undefined>(
		pendingListKey
	)

	const targetListKey = DirectDealKeys.list(marketBasketId, { status: targetStatus })
	const previousTargetListData = queryClient.getQueryData<DirectDealSummary[] | undefined>(
		targetListKey
	)

	const updatedDirectDealData = queryClient.getQueryData<DirectDeal>(
		DirectDealKeys.directDeal(marketBasketId, directDealId, { stocked: activeTab })
	)

	return {
		previousSummaryData,
		previousPendingListData,
		previousTargetListData,
		updatedDirectDealData,
	}
}

/**
 * Updates the summary cache with optimistic changes (decrements pending, increments accepted/rejected, updates savings)
 */
export const updateSummaryCache = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	status: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED,
	annualTotalSavings: number
): void => {
	queryClient.setQueryData<DirectDealsOverview | undefined>(
		DirectDealKeys.summary(marketBasketId),
		(old) => {
			if (!old) return old

			return {
				...old,
				pending: old.pending - 1,
				accepted: status === DirectDealStatus.ACCEPTED ? old.accepted + 1 : old.accepted,
				rejected: status === DirectDealStatus.REJECTED ? old.rejected + 1 : old.rejected,
				acceptedAnnualTotalSavings:
					status === DirectDealStatus.ACCEPTED
						? old.acceptedAnnualTotalSavings + annualTotalSavings
						: old.acceptedAnnualTotalSavings,
			}
		}
	)
}

/**
 * Removes a direct deal from the pending list cache
 */
export const removeFromPendingList = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	directDealId: string | number
): void => {
	const pendingListKey = DirectDealKeys.list(marketBasketId, {
		status: DirectDealStatus.PENDING,
	})

	queryClient.setQueryData<DirectDealSummary[] | undefined>(pendingListKey, (old) => {
		if (!old) return old
		return old.filter((deal) => deal.id !== Number(directDealId))
	})
}

/**
 * Adds or updates a direct deal in the target list cache (accepted/rejected) with proper sorting
 * Sorting: category priority (RECOMMEND -> DECLINE), then by annualTotalSavings (descending)
 */
export const addToTargetList = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	directDealId: string | number,
	targetStatus: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED,
	updatedDealData: DirectDeal | undefined
): void => {
	const targetListKey = DirectDealKeys.list(marketBasketId, { status: targetStatus })

	queryClient.setQueryData<DirectDealSummary[] | undefined>(targetListKey, (old) => {
		if (!updatedDealData?.summary) return old

		const updatedDeal: DirectDealSummary = {
			...updatedDealData.summary,
			status: targetStatus,
		}

		const currentList = old ?? []
		const withoutCurrent = currentList.filter((deal) => deal.id !== Number(directDealId))

		// Insert with correct sorting (category priority, then annual total savings)
		return [updatedDeal, ...withoutCurrent].sort((a, b) => {
			const categoryDiff = CATEGORY_PRIORITY[a.category] - CATEGORY_PRIORITY[b.category]
			if (categoryDiff !== 0) return categoryDiff
			return b.annualTotalSavings - a.annualTotalSavings
		})
	})
}

/**
 * Updates a direct deal in the ALL list (unfiltered) with the new status
 */
export const updateAllList = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	directDealId: string | number,
	newStatus: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED,
	updatedDealData: DirectDeal | undefined
): void => {
	const allListKey = DirectDealKeys.list(marketBasketId, { status: null })

	queryClient.setQueryData<DirectDealSummary[] | undefined>(allListKey, (old) => {
		if (!old || !updatedDealData?.summary) return old

		return old.map((deal) =>
			deal.id === Number(directDealId)
				? {
						...updatedDealData.summary,
						status: newStatus,
					}
				: deal
		)
	})
}

/**
 * Updates the detail deal cache with the new status
 */
export const updateDetailDealStatus = (
	queryClient: QueryClient,
	marketBasketId: Id,
	directDealId: Id,
	newStatus: DirectDealStatus.ACCEPTED | DirectDealStatus.REJECTED,
	updatedDealData: DirectDeal | undefined,
	activeTab: boolean | string
): void => {
	if (!updatedDealData) return

	queryClient.setQueryData(
		DirectDealKeys.directDeal(marketBasketId, directDealId, { stocked: activeTab as any }),
		(old: DirectDeal | undefined) => {
			if (!old) return old

			return {
				...old,
				summary: {
					...old.summary,
					status: newStatus,
				},
			}
		}
	)
}

/**
 * Restores the direct deal caches to their previous state in case of error
 */
export const restoreCachesFromSnapshot = (
	queryClient: QueryClient,
	snapshot: CacheSnapshot,
	marketBasketId: string | number
): void => {
	if (snapshot.previousSummaryData !== undefined) {
		queryClient.setQueryData(DirectDealKeys.summary(marketBasketId), snapshot.previousSummaryData)
	}

	if (snapshot.previousPendingListData !== undefined) {
		const pendingListKey = DirectDealKeys.list(marketBasketId, {
			status: DirectDealStatus.PENDING,
		})
		queryClient.setQueryData(pendingListKey, snapshot.previousPendingListData)
	}

	if (snapshot.previousTargetListData !== undefined) {
		const targetListKey = DirectDealKeys.list(marketBasketId, { status: snapshot.status })
		queryClient.setQueryData(targetListKey, snapshot.previousTargetListData)
	}
}

export const updateActionCenterCache = (queryClient: QueryClient, marketBasketId: Id): void => {
	const actionCenterKey = ActionCenterKeys.tasks(marketBasketId)

	queryClient.setQueryData<ActionCenterTask[] | undefined>(actionCenterKey, (old) => {
		if (!old) return old

		return old.map((task) => {
			if (task.task === ActionCenterTaskType.DIRECT_DEALS) {
				return {
					...task,
					completed: true,
				}
			}

			if (task.task === ActionCenterTaskType.CONTRACT_LOADING) {
				return {
					...task,
					isLoading: true,
				}
			}

			return task
		})
	})
}